import {parseUtcDate} from './parseUtcDate';

const countFormatter = new Intl.NumberFormat('en-US');

/**
 * @registry Formats an integer count with en-US grouping ("1,234").
 */
export const formatCount = (value: number): string =>
  countFormatter.format(value);

/**
 * @registry Capitalizes each space-separated word of a string.
 */
export const capitalize = (str?: string): string => {
  if (!str) return '';
  const strings = str.split(' ');
  return strings
    .map(string => string.charAt(0).toLocaleUpperCase() + string.slice(1))
    .join(' ');
};

/**
 * Formats a date-only field (due dates etc.) by its UTC calendar day — such fields are stored
 * as midnight-UTC instants, so local-timezone formatting shifts the day for viewers behind UTC.
 * @registry Locale date string for a UTC-naive date-only field, by UTC calendar day.
 */
export const formatUtcCalendarDate = (
  date: Date | string | undefined
): string => {
  if (!date) return '—';
  return parseUtcDate(date).toLocaleDateString(undefined, {timeZone: 'UTC'});
};

/**
 * Formats a date-only field as `MMM D, YYYY` by its UTC calendar day, or by the
 * calendar day in `timeZone` (see `formatUtcCalendarDate` for why UTC-naive fields need this).
 * @registry `MMM D, YYYY` for a UTC-naive date-only field, with fallback and optional IANA zone.
 */
export const formatUtcShortDate = (
  date: Date | string | undefined,
  fallback = '—',
  timeZone = 'UTC'
): string => {
  if (!date) return fallback;
  return parseUtcDate(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone,
  });
};

/**
 * Formats a UTC-naive timestamp field as `MMM D, YYYY h:mm A` by its UTC
 * instant (see `formatUtcCalendarDate` for why UTC-naive fields need this).
 * @registry `MMM D, YYYY h:mm A` for a UTC-naive timestamp, with fallback.
 */
export const formatUtcDateTime = (
  date: Date | string | undefined,
  fallback = '—'
): string => {
  if (!date) return fallback;
  const d = parseUtcDate(date);
  const datePart = formatUtcShortDate(d);
  const timePart = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  });
  return `${datePart} ${timePart}`;
};

/**
 * @registry `MMM D, YYYY` in viewer-local time for full timestamps.
 */
export const formatDate = (date: Date | undefined): string => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Returns the number of calendar days between the given date and today, using local-date
 * midnight-to-midnight boundaries (not 24-hour rolling) — today returns 0, yesterday returns 1.
 * @registry Calendar-day difference from today (local midnight boundaries).
 */
export const getDaysDiff = (date: Date): number => {
  const d = new Date(date);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Day-granularity relative label for activity tracking where sub-day precision isn't
 * meaningful (e.g. 'Today', '3 days ago', 'Last week', 'Over a year ago').
 * @registry Day-granularity relative label ('Today', '3 days ago', 'Never').
 */
export const formatDayRelativeOrNever = (date: Date | undefined): string => {
  if (!date) return 'Never';
  const diffDays = getDaysDiff(new Date(date));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return 'Last week';
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} weeks ago`;
  }
  if (diffDays < 60) return 'Last month';
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} months ago`;
  }
  return 'Over a year ago';
};

/**
 * @registry Compact elapsed-time label ('just now', '5m ago', '3h ago', '5d ago', '2mo ago', '1y ago').
 */
export const formatCompactAgo = (
  date: Date | undefined
): string | undefined => {
  if (!date) return undefined;
  const time = parseUtcDate(date).getTime();
  if (Number.isNaN(time)) return undefined;
  const diffMs = Math.max(0, Date.now() - time);
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

/**
 * @registry Minute/hour/day relative label ('Just now', '5 min ago', 'Never').
 */
export const formatRelativeOrNever = (date: Date | undefined): string => {
  if (!date) return 'Never';
  const d = new Date(date);
  const now = new Date();

  const diffMs = now.getTime() - d.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

  const diffDays = getDaysDiff(d);

  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays} days ago`;
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  const years = Math.floor(diffDays / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
};
